import React from 'react';
import { TouchableOpacity } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { CustomSwitch } from '@/components/CustomSwitch';
import { RadioItem } from '@/components/RadioItem';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useNonWithdrawableVerificationCheck } from '@/feature/account-details/hooks/use-non-withdrawable-verification-check';
import { useDebitCardRemovalStore } from '@/hooks/use-debit-card-removal-store';
import { LightSdd } from '@/hooks/use-user-trustly-information';
import { navigateToKyc } from '@/navigation/navigation';
import { IdpvSource } from '@/navigation/types';

import { IDPV_STATUS_OVERRIDES, useIdpvStatusOverrideStore } from '../hooks/use-idpv-status-override-store';
import { TrustlyInfoOverrideToggleKey, useTrustlyInfoOverrideStore } from '../hooks/use-trustly-info-override-store';

const TOGGLE_KEYS: TrustlyInfoOverrideToggleKey[] = ['TRUSTLY_VERIFIED', 'HAS_RESTRICTED_BALANCE', 'IS_IDPV'];

/**
 * Dev controls to simulate any `/auth/user/trustly-info` state, so the withdraw
 * verification gate (choice screen / trustly-only / idpv-only / blocked) can be
 * tested without back-office setup, and to force the IDPV outcome so the
 * success / failure branches can be exercised without the provider's verdict.
 */
export const WithdrawVerificationDebug = () => {
    const navigation = useNavigation();
    const enabled = useTrustlyInfoOverrideStore(state => state.enabled);
    const values = useTrustlyInfoOverrideStore(state => state.values);
    const { setEnabled, setLightSdd, toggleValue, reset } = useTrustlyInfoOverrideStore(state => state.actions);
    const idpvOverrideEnabled = useIdpvStatusOverrideStore(state => state.enabled);
    const idpvOverrideStatus = useIdpvStatusOverrideStore(state => state.status);
    const {
        setEnabled: setIdpvOverrideEnabled,
        setStatus: setIdpvOverrideStatus,
        reset: resetIdpvOverride,
    } = useIdpvStatusOverrideStore(state => state.actions);
    const { outcome } = useNonWithdrawableVerificationCheck();

    const resetOverrides = () => {
        reset();
        resetIdpvOverride();
    };

    // Skips the scan + provider webview: lands directly on the verifying screen with the
    // withdraw source set, the same way ScanId does before launching the webview.
    const jumpToWithdrawIdpvVerifying = () => {
        useDebitCardRemovalStore.getState().setVerificationSource(IdpvSource.WITHDRAW);
        navigateToKyc(navigation, { screen: 'KycVerifyingIdpv' });
    };

    return (
        <Box>
            <Text color="neutral300" marginVertical="s4">
                Gate outcome on Withdraw press: <Text color={ds.colorToken.text.primary}>{outcome}</Text>
            </Text>
            <Row marginVertical="s8" alignItems="center">
                <CustomSwitch val={enabled} handleOnPress={() => setEnabled(!enabled)} />
                <SizedBox value={8} />
                <Text>Override /auth/user/trustly-info</Text>
            </Row>
            <Text color="neutral300" marginVertical="s4">
                LIGHT_SDD
            </Text>
            {Object.values(LightSdd).map(value => (
                <TouchableOpacity key={value} onPress={() => setLightSdd(value)}>
                    <Row alignItems="center" marginVertical="s4">
                        <RadioItem selected={values.LIGHT_SDD === value} />
                        <SizedBox value={8} />
                        <Text>{value}</Text>
                    </Row>
                </TouchableOpacity>
            ))}
            <SizedBox value={8} />
            {TOGGLE_KEYS.map(key => (
                <Row key={key} marginVertical="s8" alignItems="center">
                    <CustomSwitch val={values[key]} handleOnPress={() => toggleValue(key)} />
                    <SizedBox value={8} />
                    <Text>{key}</Text>
                </Row>
            ))}
            <SizedBox value={16} />
            <Row marginVertical="s8" alignItems="center">
                <CustomSwitch
                    val={idpvOverrideEnabled}
                    handleOnPress={() => setIdpvOverrideEnabled(!idpvOverrideEnabled)}
                />
                <SizedBox value={8} />
                <Text>Override IDPV status</Text>
            </Row>
            {IDPV_STATUS_OVERRIDES.map(status => (
                <TouchableOpacity key={status} onPress={() => setIdpvOverrideStatus(status)}>
                    <Row alignItems="center" marginVertical="s4">
                        <RadioItem selected={idpvOverrideStatus === status} />
                        <SizedBox value={8} />
                        <Text>{status}</Text>
                    </Row>
                </TouchableOpacity>
            ))}
            <SizedBox value={8} />
            <Button label="Reset overrides" onPress={resetOverrides} />
            <SizedBox value={8} />
            <Button
                label="Go to Withdraw Verification Selector"
                onPress={() => navigateToKyc(navigation, { screen: 'VerificationSelector' })}
            />
            <SizedBox value={8} />
            <Button
                label="Start IDPV (withdraw source)"
                onPress={() =>
                    navigateToKyc(navigation, { screen: 'KycScanId', params: { source: IdpvSource.WITHDRAW } })
                }
            />
            <SizedBox value={8} />
            <Button label="Jump to IDPV verifying screen (withdraw source)" onPress={jumpToWithdrawIdpvVerifying} />
        </Box>
    );
};
